import { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonIcon, Dropdown } from '@quintoandar-tokko/jopijs';
import { normalizeText, sortByKeyEs } from '@/components/FilterBar/utils';
import JopiDropdownItems from '@/components/shared/JopiDropdownItems/JopiDropdownItems';
import Trans from '@/components/shared/Trans';
import translations from './SearchableMultiSelectDropdown.trans.json';
import styles from './SearchableMultiSelectDropdown.module.scss';
import { getIsAllItemsSelected, getIsPartiallySelected } from './utils';

/**
 * Resets draft state when the dropdown panel unmounts (jopi hides Dropdown.Items
 * when closed). Skips reset after Apply so we do not race parent state updates.
 */
function ResetDraftOnClose({
  committedSelection,
  onDraftChange,
  skipDraftResetRef,
  resetPanelStateRef,
}) {
  const committedSelectionRef = useRef(committedSelection);
  const onDraftChangeRef = useRef(onDraftChange);
  committedSelectionRef.current = committedSelection;
  onDraftChangeRef.current = onDraftChange;

  useEffect(
    () => () => {
      if (!skipDraftResetRef.current) {
        onDraftChangeRef.current(committedSelectionRef.current);
        resetPanelStateRef.current();
      }

      skipDraftResetRef.current = false;
    },
    [resetPanelStateRef, skipDraftResetRef]
  );

  return null;
}

function SearchableMultiSelectDropdown({
  items = [],
  draftSelection = [],
  committedSelection = [],
  onDraftChange,
  onApply,
  labels = {},
  className = '',
  width,
  itemsClassName = '',
  multiselectClassName = '',
  itemsHeight = 336,
  renderTrigger,
  showSearch = true,
  showOnlyAction = true,
  preserveOrder = false,
  forceDropDown = false,
}) {
  const [searchText, setSearchText] = useState('');
  const skipDraftResetRef = useRef(false);
  const resetPanelStateRef = useRef(() => {});
  resetPanelStateRef.current = () => setSearchText('');
  const normalizedSearch = normalizeText(searchText) ?? '';

  const selectableItems = items.filter(
    (item) => item?.id != null && (item.text ?? '').trim() !== ''
  );

  const {
    allLabel,
    applyLabel = Trans(translations, 'SearchableMultiSelectApply'),
    searchPlaceholder,
    noResultsLabel = Trans(translations, 'SearchableMultiSelectNoResultsFound'),
    onlyLabel = Trans(translations, 'SearchableMultiSelectOnly'),
  } = labels;

  const matchingItems = selectableItems.filter((item) =>
    (normalizeText(item.text) ?? '').includes(normalizedSearch)
  );
  const filteredItems = preserveOrder
    ? matchingItems
    : sortByKeyEs(matchingItems, 'text');

  const isAllLocalSelected = getIsAllItemsSelected(
    draftSelection,
    selectableItems
  );

  const isAllPartiallySelected = getIsPartiallySelected(
    draftSelection,
    selectableItems
  );

  const selectAll = () => {
    if (!selectableItems.length) return;

    if (isAllLocalSelected) {
      onDraftChange([]);
      return;
    }

    onDraftChange(selectableItems);
  };

  const selectOnly = (item) => {
    onDraftChange([item]);
  };

  const isItemActive = (id) => draftSelection.some((entry) => entry.id === id);

  const isOnlySelected = (id) =>
    draftSelection.length === 1 && draftSelection[0].id === id;

  const handleToggle = (item) => {
    const isSelected = draftSelection.find((entry) => entry.id === item.id);

    if (isSelected) {
      onDraftChange(draftSelection.filter((entry) => entry.id !== item.id));
      return;
    }

    onDraftChange([...draftSelection, item]);
  };

  const handleApply = () => {
    skipDraftResetRef.current = true;
    setSearchText('');
    onApply();
  };

  return (
    <Dropdown height="auto" className={className} width={width}>
      {renderTrigger()}
      <JopiDropdownItems
        height={itemsHeight}
        clickClose
        forceDropDown={forceDropDown}
        className={`${styles.items} ${itemsClassName}`}
        data-testid="searchable-multiselect-menu"
      >
        <ResetDraftOnClose
          committedSelection={committedSelection}
          onDraftChange={onDraftChange}
          skipDraftResetRef={skipDraftResetRef}
          resetPanelStateRef={resetPanelStateRef}
        />
        {showSearch && (
          <Dropdown.Search
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            suffix={
              searchText !== '' && (
                <ButtonIcon
                  variant={['transparentIcon', 'iconExtraSmall22px']}
                  icon="icon-cerrar"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSearchText('');
                  }}
                />
              )
            }
          />
        )}
        {!searchText && allLabel && (
          <Dropdown.Multiselect
            key={`all-items-${isAllLocalSelected ? '1' : '0'}-${
              isAllPartiallySelected ? '1' : '0'
            }`}
            onClick={(event) => {
              event.stopPropagation();
              selectAll();
            }}
            active={isAllLocalSelected || isAllPartiallySelected}
            data-testid="searchable-multiselect-all-items"
            className={`${styles.multiselectRow} ${multiselectClassName}${
              isAllPartiallySelected ? ` ${styles.multiselectRowPartial}` : ''
            }`}
          >
            {allLabel}
          </Dropdown.Multiselect>
        )}

        {filteredItems.map((item) => {
          const isActive = isItemActive(item.id);
          const canSelectOnly = !isOnlySelected(item.id);

          const handleOnly = (event) => {
            event.stopPropagation();
            selectOnly(item);
          };

          return (
            <Box
              key={`${item.id}-${isActive ? '1' : '0'}`}
              className={styles.itemRow}
            >
              <Dropdown.Multiselect
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggle(item);
                }}
                active={isActive}
                className={`${styles.multiselectRow} ${multiselectClassName}`}
              >
                {item.text}
              </Dropdown.Multiselect>
              {showOnlyAction && (
                <Box
                  as="span"
                  role="button"
                  tabIndex={canSelectOnly ? 0 : -1}
                  aria-hidden={!canSelectOnly}
                  className={`${styles.onlyAction}${
                    canSelectOnly ? '' : ` ${styles.onlyActionHidden}`
                  }`}
                  onClick={handleOnly}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleOnly(event);
                    }
                  }}
                >
                  {onlyLabel}
                </Box>
              )}
            </Box>
          );
        })}

        {filteredItems.length === 0 && (
          <Dropdown.Text>{noResultsLabel}</Dropdown.Text>
        )}

        <Box className={styles.applyButtonContainer}>
          <Button
            className={styles.applyButton}
            variant={['primary', 'small']}
            onClick={handleApply}
          >
            {applyLabel}
          </Button>
        </Box>
      </JopiDropdownItems>
    </Dropdown>
  );
}

export default SearchableMultiSelectDropdown;
